<template>
  <validation-observer tag="div" ref="form">
    <validation-provider vid="ticket_or_sales_plan_selection" :name="$t('sales_plans.sales_plan_id')" :rules="model.id ? '' : 'required'" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="`${$t('sales_plans.sales_plan_id')}*`"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
            :key="`${_uid}-ticket-or-sales-plan`"
            name="ticket_or_sales_plan_selection"
            v-model="model.ticket_or_sales_plan_selection"
            :custom-label="ticketOrSalesPlanOptionLabel"
            url="/v1/trips/ticket-or-sales-plan-options"
            :params="{
              id__in: initialTicketOrSalesPlanSelection || undefined,
            }"
            :disabled="!!model.id"
            @update:selected="onTicketOrSalesPlanSelected"
        >
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="selected_purchase_orders" :name="lodash.startCase($t('purchase_payments.purchase_order_id'))" rules="required" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.startCase($t('purchase_payments.purchase_order_id')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
            :key="`purchase-payment-${model.ticket_or_sales_plan_selection}`"
            name="purchase_order_id"
            v-model="model.purchase_order_id"
            :multiple="true"
            :url="`/purchase_orders`"
            :modify-response="response => {
              const { data: { data } } = response;
              if (data.length) {
                const dataEdited = data.map(datum => ({
                ...datum,
                 $isDisabled: datum.total_price <= datum.total_payment
                }))
                const dataEnabled = dataEdited.filter(datum => !datum.$isDisabled)
                const dataDisabled = dataEdited.filter(datum => datum.$isDisabled)
                response.data.data = [
                    ...dataEnabled,
                    ...dataDisabled,
                ]
              }
              return response;
            }"
            :params="model.id ? {
               with: 'customer,shrimp,purchase_items.purchase_order.farmer,purchase_items.purchase_order.product',
               ...(model.ticketable.ticket_id
                 ? { where_has: `tickets,id,${model.ticketable.ticket_id}` }
                 : (model.sales_plan_id ? {where_has: `purchase_plan,sales_plan_id,${model.sales_plan_id}`} : {})),
               product_code__not_in: 'down_payment,hybrid',
            } : {
               with: 'customer,shrimp,purchase_items.purchase_order.farmer,purchase_items.purchase_order.product',
               ...(model.ticketable.ticket_id
                 ? { where_has: `tickets,id,${model.ticketable.ticket_id}` }
                 : (model.sales_plan_id ? {where_has: `purchase_plan,sales_plan_id,${model.sales_plan_id}`} : {})),
               product_code__not_in: 'down_payment,hybrid',
               stock_available: 1,
            }"
            :custom-label="(m) => `${m.id} (${m.customer.name} - ${$moment.utc(m.ordered_at).local().format('DD MMM YYYY')})`"
            :disabled="!!model.id"
            @update:selected="onPurchaseOrderSelected"
        >
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <b-form-group
        class="row mb-3"
        label-cols-md="2"
        :label="lodash.upperFirst($t('products.singular')) + '*'"
    >
      <b-form-input
          name="shrimp_name"
          type="text"
          disabled
          :value="purchaseOrderServices.length > 0 ? purchaseOrderServices.join(', ') : '-'"
      ></b-form-input>
    </b-form-group>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trips.invoice_number'))"
                         rules="required"
                         vid="invoice_number">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.startCase($t('trips.invoice_number')) +  '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <b-form-input
            name="invoice_number"
            type="text"
            :state="errors[0] ? false : (validated ? true : null)"
            v-model="model.invoice_number"
        ></b-form-input>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="tripped_at" :name="lodash.startCase($t('trips.tripped_at'))"
                         :rules="trippedAtRules"
                         v-slot="{ validated, errors }"
    >
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.startCase($t('trips.tripped_at')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <date-picker
            name="tripped_at"
            format="DD MMMM YYYY"
            type="date"
            required
            :state="errors[0] ? false : (validated ? true : null)"
            v-model="model.tripped_at"
            @input="model.tripped_at = $event"
        >
        </date-picker>
      </b-form-group>
    </validation-provider>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trips.vehicle'))" rules="required"
                         vid="vehicle">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.startCase($t('trips.vehicle')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
            :key="`${_uid}-vehicles`"
            name="vehicle_id"
            v-model.number="model.vehicle_id"
            :custom-label="vehicleLabel"
            url="/vehicles"
        >
          <template v-slot:beforeList>
            <b-button variant="link" v-b-modal.vehicle-create-modal
            >+ {{ $t('Add new {something}',  { something: $t('trips.vehicle') }) }}</b-button>
            <vehicle-create-modal
                :key="`vehicle-create-modal-${model.vehicle_id}`"
                :id="`vehicle-create-modal`"
                @created="$set(model, 'vehicle_id', $event.id)"
            ></vehicle-create-modal>
          </template>
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trips.driver'))" rules="required"
                         vid="driver">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.startCase($t('trips.driver')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
            name="driver_id"
            v-model.number="model.driver_id"
            :custom-label="(m) => `${m.name} - ${m.phone_number}`"
            url="/drivers"
        >
          <template v-slot:beforeList>
            <b-button variant="link" v-b-modal.driver-create-modal
            >+ {{ $t('Add new {something}',  { something: $t('trips.driver') }) }}</b-button>
            <driver-create-modal
                :key="`driver-create-modal-${model.driver_id}`"
                :id="`driver-create-modal`"
                @created="$set(model, 'driver_id', $event.id)"
            ></driver-create-modal>
          </template>
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider
        :name="lodash.upperFirst($t('trips.to_processing'))" rules="required" vid="to_processing"
        v-slot="{ validated, errors }"
    >
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.startCase($t('trips.to_processing')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
            :key="`${_uid}-customer_id`"
            name="customer_id"
            v-model.number="model.customer_id"
            :custom-label="(m) => `${m.name} (${m.code})`"
            url="/customers"
            :params="{
              type: 'processing',
            }"
            @update:selected="$set(model, 'customer', $event)"
        >
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider
        v-slot="{ validated, errors }"
        :name="lodash.upperFirst($t('purchase_orders.file'))"
        rules="mimes:image/jpeg,image/png,application/pdf"
        vid="files.0"
    >
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('purchase_orders.file'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <b-form-file
            :name="`files.${0}`"
            v-model="model.files[0].file"
            :state="errors[0] ? false : (validated ? true : null)"
            :placeholder="filePlaceholder"
            :drop-placeholder="lodash.upperFirst($t('drop file here'))"
            accept="image/jpeg,image/png,application/pdf"
            width="100%"
            plain
            @change="onChangeFile($event, 0)"
        >
        </b-form-file>
        <file-item
            class="mt-2"
            v-if="model.files[0] && !errors.length"
            :file="model.files[0]"
        />
      </b-form-group>
    </validation-provider>
    <hr>
    <validation-provider vid="has_trip_item" :name="lodash.upperFirst($t('trips.creates.trip_item'))" rules="required" v-slot="{ validated, errors }">
      <b-form-group
        class="row mb-3"
        label-cols-md="2"
        :label="`${lodash.startCase($t('trips.creates.trip_item'))}*`"
        :state="errors[0] ? false : (validated ? true : null)"
        :invalid-feedback="errors[0]"
    >
      <b-button variant="primary" class="mb-2" v-b-modal.modal-purchase-item :disabled="(model.selected_purchase_orders || []).length === 0">{{ $t('Click to Choose {something}', {something: lodash.startCase($t(`trips.creates.trip_item`))}) }}</b-button>
        <b-input :value="model.has_trip_item ? 'filled':''" v-show="false"></b-input>
      <modal-select-item-purchase-order
          v-if="model.purchase_items.length"
          :key="`modal-purchase-item-${model.selected_purchase_orders.map(v => v.id).join(',')}`"
          id="modal-purchase-item"
          modal-title="Pilih Item Nota Panen"
          :fields="purchaseItemTableFields"
          :items="model.purchase_items"
          @update:items="handleChangePurchaseItems"
      />
      <template v-for="(tripItem, key) in model.trip_items">
        <div :key="`trip_items.key.${key}`" :class="`row g-2 ${key === 0 ? '' : 'mt-2'}`">
          <div class="col-1">
            <div :class="key===0?'mb-3':'mb-2'">{{key === 0 ? 'No': '&nbsp;'}}</div>
            <div>{{key+1}}</div>
          </div>
          <div class="col-11">
            <div class="row">
              <b-form-group
                  class="col-md-2"
                  :label="`${lodash.startCase($t('trip_items.purchase_order_id'))}*`"
              >
                <b-form-input
                    disabled
                    :value="`${tripItem.purchase_order_id}`"
                />
              </b-form-group>
              <b-form-group
                  class="col-md-2"
                  :label="`${lodash.startCase($t('products.singular'))}*`"
              >
                <b-form-input
                    disabled
                    :value="`${lodash.startCase(tripItem.purchase_order.product.name)}`"
                />
              </b-form-group>
              <b-form-group
                  class="col-md-2"
                  :label="`${lodash.startCase($t('trip_items.quality'))}*`"
              >
                <b-form-input
                    disabled
                    :value="`${$t('qualities.'+tripItem.quality)}`"
                />
              </b-form-group>
              <validation-provider
                  class="col-md-2 mb-3" :vid="`trip_items.${key}.quantity`"
                  :name="lodash.startCase($t('trip_items.quantity'))"
                  :rules="`required|gt:0|max_value:${tripItem.max_quantity}`"
                  v-slot="{ validated, errors }">
                <b-form-group
                    :label="`${lodash.startCase($t('trip_items.quantity'))}*`"
                    :state="errors[0] ? false : (validated ? true : null)"
                    :invalid-feedback="errors[0]"
                >
                  <b-input-group :append="$t('units.kg')">
                    <numeral-input
                        id="quantity"
                        :name="`trip_items[${key}][quantity]`"
                        style="text-align: right"
                        :placeholder="lodash.startCase($t('trip_items.quantity'))"
                        :state="errors[0] ? false : (validated ? true : null)"
                        v-model="tripItem['quantity']"
                        @blur="handleChangeQuantity(key)"
                        @input="model.isTripItemChanged = true"
                    />
                  </b-input-group>
                </b-form-group>
              </validation-provider>
              <validation-provider class="col-md-2 mb-3" :vid="`trip_items.${key}.size`" :name="lodash.startCase($t('trip_items.size'))" rules="required|gt:0" v-slot="{ validated, errors }">
                <b-form-group
                    :label="`${lodash.startCase($t('trip_items.size'))}*`"
                    :state="errors[0] ? false : (validated ? true : null)"
                    :invalid-feedback="errors[0]"
                >
                  <b-input-group>
                    <numeral-input
                        id="size"
                        :name="`trip_items[${key}][size]`"
                        style="text-align: right"
                        :placeholder="lodash.startCase($t('trip_items.size'))"
                        :state="errors[0] ? false : (validated ? true : null)"
                        v-model="tripItem['size']"
                    />
                  </b-input-group>
                </b-form-group>
              </validation-provider>
              <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trip_items.label'))"
                                   :vid="`trip_items.${key}.label`" class="col-md-2 mb-3"
                                   rules="required">
                <b-form-group
                    :label="lodash.startCase($t('trip_items.label')) + '*'"
                    :state="errors[0] ? false : (validated ? true : null)"
                    :invalid-feedback="errors[0]"
                >
                  <b-form-input
                      id="label"
                      :name="`trip_items[${key}][label]`"
                      :placeholder="lodash.startCase($t('trip_items.label'))"
                      :state="errors[0] ? false : (validated ? true : null)"
                      v-model="tripItem['label']"
                      :formatter="value => value.trimStart().trimEnd().toUpperCase()"
                      lazy-formatter
                  ></b-form-input>
                </b-form-group>
              </validation-provider>
              <validation-provider
                  class="col-md-3 mb-3" :vid="`trip_items.${key}.unit_price`"
                  :name="lodash.startCase($t('trip_items.unit_price'))"
                  v-slot="{validated,errors}"
                  :rules="tripItem.purchase_order.type==='deposit_goods'?`required`:`required|gt:0`"
              >
                <b-form-group
                    :label="`${lodash.startCase($t('trip_items.unit_price'))}*`"
                    :state="errors[0] ? false : (validated ? true : null)"
                    :invalid-feedback="errors[0]"
                >
                  <numeral-input
                      id="unit_price"
                      :name="`trip_items[${key}][unit_price]`"
                      required
                      style="text-align: right"
                      :placeholder="lodash.startCase($t('trip_items.unit_price'))"
                      :state="errors[0] ? false : (validated ? true : null)"
                      v-model="tripItem['unit_price']"
                      format="$ 0,0.[00]"
                      lazy-formatter
                      @blur="updateTotalPriceItem(key)"
                      disabled="true"
                  />
                </b-form-group>
              </validation-provider>
              <validation-provider
                  class="col-md-3 mb-3" :vid="`trip_items.${key}.total_price`"
                  :name="lodash.startCase($t('trip_items.total_price'))"
                  v-slot="{ validated, errors }"
                  :rules="tripItem.purchase_order.type==='deposit_goods'?`required`:`required|gt:0`"
              >
                <b-form-group
                    :label="`${lodash.startCase($t('trip_items.total_price'))}*`"
                    :state="errors[0] ? false : (validated ? true : null)"
                    :invalid-feedback="errors[0]"
                >
                  <numeral-input
                      id="total_unit_price"
                      :name="`trip_items[${key}][total_price]`"
                      v-model="tripItem.total_price"
                      style="text-align: right"
                      :placeholder="lodash.startCase($t('trip_items.total_price'))"
                      :state="errors[0] ? false : (validated ? true : null)"
                      format="$ 0,0.[00]"
                      lazy-formatter
                      :disabled="true"
                  />
                </b-form-group>
              </validation-provider>
              <validation-provider
                  class="col-md-3 mb-3"
                  :vid="`trip_items.${key}.farmer`"
                  :name="lodash.startCase($t('trip_items.farmer'))"
                  v-slot="{ validated, errors }"
              >
                <b-form-group
                    :label="`${lodash.startCase($t('trip_items.farmer'))}*`"
                    :state="errors[0] ? false : (validated ? true : null)"
                    :invalid-feedback="errors[0]"
                >
                  <b-form-input
                      :key="`${_uid}-farmers`"
                      name="farmer"
                      :state="errors[0] ? false : (validated ? true : null)"
                      :value="farmerLabel(tripItem)"
                      disabled
                  />
                </b-form-group>
              </validation-provider>
            </div>
          </div>
        </div>
      </template>
    </b-form-group>
    </validation-provider>
    <div class="row">
      <b-form-group
          class="col-md-6 mb-3 pe-md-0"
          label-cols-md="4"
          :label="`${lodash.startCase($t('trips.total_item_overall'))}:`"
      >
        <b-form-input
            :name="`totalAllItem`"
            :value="model.trip_items.length"
            style="text-align: right"
            disabled
        ></b-form-input>
      </b-form-group>
      <b-form-group
          class="col-md-6 mb-3"
          label-cols-md="4"
          :label="`${lodash.startCase($t('trips.total_quantity_overall'))}:`"
      >
        <b-input-group :append="$t('units.kg')">
          <b-form-input
              :name="`totalAllBiomassa`"
              :value="$numeral(model.tripItemTotalQuantity).format('0,0.[000]')"
              style="text-align: right"
              disabled
          ></b-form-input>
        </b-input-group>
      </b-form-group>
    </div>
    <b-form-group
        class="row mb-3"
        label-cols-md="2"
        label="Sisa Biomassa Nota Panen - Biomassa Nota Armada"
    >
      <b-input-group :append="$t('units.kg')">
        <b-form-input
            name="difference"
            :value="$numeral(purchaseOrderTotalAvailableQuantity - tripItemTotalQuantity).format('0,0.[000]')"
            style="text-align: right"
            disabled
        ></b-form-input>
      </b-input-group>
    </b-form-group>
    <hr>
    <validation-provider vid="description" :name="lodash.startCase($t('models.description'))" rules="" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          :label="lodash.startCase('catatan')"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <editor
            ref="editor"
            v-if="$parent.$parent.$options.name !== 'BModal'"
            class="form-control"
            :init="editorInit"
            inline
            plugins="lists link image paste help wordcount"
            toolbar="undo redo | styleselect | fontselect | bold italic | alignleft aligncenter alignright alignjustify | outdent indent"
            v-model="model.description"
        />
        <b-form-textarea v-else v-model="model.description" />
      </b-form-group>
    </validation-provider>
  </validation-observer>
</template>

<script>
import FormModel from './model'
import ModalSelectItemPurchaseOrder from '@/components/modals/modal-select-item-purchase-order'
import VehicleCreateModal from '@/components/modals/vehicle-create'
import DriverCreateModal from '@/components/modals/driver-create'
import FileItem from "@/components/inputs/file-item.vue";
import {salesPlanSelectLabel} from "@/helpers/ui/salesPlanLabels";

export default {
  extends: FormModel,
  components: {
    FileItem,
    DriverCreateModal,
    VehicleCreateModal,
    ModalSelectItemPurchaseOrder
  },
  data() {
    return {
      model: {
        ...this.value,
        isTripItemChanged: false,
      },
      ...this.value,
      editInit: true,
      selectedPurchaseItems: [],
      quality_options: [
        { value: 'fresh', text: 'Fresh' },
        { value: 'km', text: 'Kulit Muda (KM)' },
        { value: 'bs', text: 'Below Standard (BS)' }
      ],
      funding_options: [
        { value: 'fresh', text: 'Fresh' },
        { value: 'kmtb', text: 'Titip Bongkar' }
      ],
      firstFile: null,
      origin_trip_items: [
        ...((this.value.trip_items || []).length ? this.value.trip_items : []),
      ],
      initialTicketOrSalesPlanSelection: null,
    }
  },
  computed: {
    isCreateTripAgain() {
      return !!localStorage.getItem('createTripAgain')
    },
    modelDefaultValue() {
      let defaultValue = {
        purchase_items: [],
        purchase_order_id: [],
        files: [
          ...((this.value.files || []).length ? this.value.files : [{file:null}]),
        ],
        trip_items: [
          ...((this.value.trip_items || []).length ? this.value.trip_items : []),
        ],
        selected_purchase_orders: [
          ...((this.value.selected_purchase_orders || []).length ? this.value.selected_purchase_orders : []),
        ],
        sales_plan_id: this.value.sales_plan_id || null,
        sales_plan: !this.lodash.isEmpty(this.value.sales_plan) ? this.value.sales_plan : {},
        difference: 0,
      }
      let newModelValue = localStorage.getItem('createTripAgain')

      if (newModelValue) {
        newModelValue = JSON.parse(newModelValue)
        defaultValue = {
          ...defaultValue,
          ...newModelValue,
        }
      }
      return this.lodash.cloneDeep(defaultValue)
    },
    purchaseOrderTotalAvailableQuantity() {
      return this.model.selected_purchase_orders.reduce((total, purchaseOrder) => {
        // let totalAvailableQuantity = purchaseOrder.purchase_item_total_available_quantity || 0
        const totalAvailableQuantity = purchaseOrder.purchase_items.reduce((total, purchaseItem) => {
          return total + purchaseItem.available_quantity
        }, 0)
        return total + (totalAvailableQuantity > 0 ? totalAvailableQuantity : 0)
      }, 0)
    },
    purchaseOrderServices() {
      return this.lodash.isEmpty(this.model.selected_purchase_orders) ? [] : [
        ...new Set(this.model.selected_purchase_orders.map(purchaseOrder => purchaseOrder.product.name))
      ]
    },
    defaultValueModalGenerate() {
      if (this.model.trip_items.length) return this.model.trip_items.map(i => i.purchase_item_id)
      else return []
    },
    purchaseItemTableFields() {
      return [
        {
          key: 'index',
          label: `${this.lodash.startCase(this.$t('trips.choose'))}`
        },
        {
          key: 'fund',
          label: `${this.lodash.startCase(this.$t('purchase_items.fund'))}`,
          formatter: (value) => this.lodash.startCase(value)
        },
        {
          key: 'quantity',
          label: `${this.lodash.startCase(this.$t('purchase_items.quantity'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'available_quantity',
          label: `${this.lodash.startCase(this.$t('purchase_items.available_quantity'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'size',
          label: `${this.lodash.startCase(this.$t('purchase_items.size'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'unit_price',
          label: `${this.lodash.startCase(this.$t('purchase_items.unit_price'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'total_price',
          label: `${this.lodash.startCase(this.$t('purchase_items.total_price'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
      ]
    },
    tripItemTotalQuantity() {
      return this.model.trip_items.reduce((total, tripItem) => total + tripItem.quantity, 0)
    },
    minimumDate(){
      const purchaseOrder = this.model.selected_purchase_orders;
      const latestPurchaseOrder = purchaseOrder.sort((a, b) => new Date(b.ordered_at) - new Date(a.ordered_at))?.[0] || {};
      return latestPurchaseOrder
    },
    filePlaceholder() {
      if (!this.model.files[0]) {
        return this.lodash.upperFirst(this.$t('choose a file or drop it here'))
      }
      return Object.keys(this.model.files[0]).length > 0 ? this.model.files[0].name : this.lodash.upperFirst(this.$t('choose a file or drop it here'))
    },
    trippedAtRules() {
      return {
        required: true,
        after_or_equal: this.$moment(this.minimumDate.ordered_at).format('YYYY-MM-DD'),
        before_or_equal: this.$moment().format("YYYY-MM-DD"),
      }
    }
  },
  watch: {
    'model.purchase_items': {
      deep: true,
      handler: function (val, oldVal) {
        if (!this.lodash.isEqual(val, oldVal)) {
          this.selectedPurchaseItems = this.lodash.cloneDeep(val.filter(purchaseItem => purchaseItem.$checkbox && purchaseItem.$selected))
        }
      }
    },
    'selectedPurchaseItems': function (val, oldVal) {
      if (!this.lodash.isEqual(val, oldVal)) {
        if(this.editInit) return;
        // eslint-disable-next-line no-unused-vars
        const initialPurchaseOrders = this.model.initial_trip_items.map((tripItem) => tripItem.purchase_order);
        let tripItems = this.lodash.cloneDeep(val.map(v => {
          return {
            purchase_order: v.purchase_order,
            purchase_order_id: v.purchase_order_id,
            purchase_item_id: v.id,
            fund: v.fund,
            quality: v.quality,
            size: v.size,
            quantity: v.available_quantity,
            max_quantity: v.available_quantity,
            unit_price: v.unit_price,
            total_price: v.total_price,
            customer_id: v.purchase_order.farmer_id,
          }
        }))
        this.$set(this.model, 'trip_items',tripItems)
      }
    },
    'model.trip_items': {
      handler: function (val, oldVal) {
        if (!this.lodash.isEqual(val, oldVal)) {
          this.model.has_trip_item = !!val.length
        }
      },
      deep: true,
    },
    'model.selected_purchase_orders': function (val, oldVal) {
      if (!this.lodash.isEqual(val, oldVal)) {
        this.model.purchase_items = this.getPurchaseItemTableItems(val)
        this.selectedPurchaseItems = []
      }
    },
  },
  created () {
    this.selectedPurchaseItems = []
    // this.$set(this, 'model', this.modelDefaultValue)
    if (this.lodash.isEmpty(this.model.ticketable)) {
      this.$set(this.model, 'ticketable', { ticket_id: null })
    }
    this.$set(this.model, 'ticket_or_sales_plan_selection', this.model.ticketable.ticket_id
      ? `ticket:${this.model.ticketable.ticket_id}`
      : (this.model.sales_plan_id ? `sales_plan:${this.model.sales_plan_id}` : null))
    this.initialTicketOrSalesPlanSelection = this.model.ticket_or_sales_plan_selection
  },
  methods: {
    farmerLabel(tripItem) {
      return tripItem.purchase_order.farmer?.name ?? '-'
    },
    ticketOrSalesPlanOptionLabel(option) {
      if (!option) return ''
      if (option.type === 'ticket') {
        const creatorName = option.creator?.name
        const label = `${option.ticket_id}: ${this.$t(`tickets.customer_types.${option.customer_type}`)}`
        if (!creatorName) return label
        return `${label}, ${this.$t('created_by_{name}_on_{date}', {
          name: creatorName,
          date: option.created_at ? this.$moment.utc(option.created_at).local().format('DD MMMM YYYY') : '-',
        })}`
      }
      return salesPlanSelectLabel({
        id: option.sales_plan_id,
        name: option.name,
        product: option.product,
        partner: option.partner,
      })
    },
    onTicketOrSalesPlanSelected(option) {
      if (option && option.type === 'ticket') {
        this.$set(this.model, 'sales_plan_id', null)
        this.$set(this.model.ticketable, 'ticket_id', option.ticket_id)
      } else if (option) {
        this.$set(this.model.ticketable, 'ticket_id', null)
        this.$set(this.model, 'sales_plan_id', option.sales_plan_id)
      } else {
        this.$set(this.model.ticketable, 'ticket_id', null)
        this.$set(this.model, 'sales_plan_id', null)
      }

      if (this.editInit) {
        this.model.purchase_order_id = (this.model.trip_items.map(item => item.purchase_order?.id));
        return;
      }

      this.model = {
        ...this.model,
        invoice_number: null,
        tripped_at: null,
        driver_id: null,
        vehicle_id: null,
        files: [{}],
        purchase_order_id: [],
        selected_purchase_orders: [],
        trip_items: [],
      }
    },
    vehicleLabel(vehicle) {
      let vehicleName = vehicle?.name

      if (vehicle?.type === 'heavy') vehicleName = this.$t('vehicles.truck')
      else if (vehicle?.type === 'medium') vehicleName = this.$t('vehicles.crank_truck')
      else if (vehicle?.type === 'light') vehicleName = this.$t('vehicles.pickup')

      return vehicle ? `${this.lodash.upperFirst(vehicleName)} - ${vehicle.license_plate}` : ''
    },
    quantityMaxValueCalculation(purchaseItemId) {
      let max_limit = this.purchaseItemOptions.find(i => i.purchase_item_id === purchaseItemId)
      if (max_limit && max_limit.quantity) return max_limit.quantity
      else return 100000000000000
    },
    onPurchaseOrderSelected(payload) {
      payload = payload.map((purchaseOrder) => {
        purchaseOrder.purchase_items = purchaseOrder.purchase_items.map(
          (purchaseItem) => {
            // find trip_items in model.trips where purchase_item_id === purchaseItem.id
            const trips = (this.model.trips || []).map((trip) => {
              return trip.trip_items.find((tripItem) => {
                return tripItem.purchase_item_id === purchaseItem.id;
              });
            });
            const trip_items = trips.filter((trip) => {
              return trip !== undefined;
            });  
            purchaseItem.available_quantity += trip_items.reduce(
              (total, tripItem) => {
                return total + tripItem.quantity;
              },
              0
            );
            return purchaseItem;
          }
        );
        this.model.trip_items = this.model.trip_items.map((tripItem) => {

          const purchaseItem = purchaseOrder.purchase_items.find(
            (purchaseItem) => {
              return purchaseItem.id === tripItem.purchase_item_id;
            }
          );
          if (purchaseItem) {
            tripItem.max_quantity = purchaseItem.available_quantity;
          }
          return tripItem;
        });
        return purchaseOrder;
      });
      // this.$set(this.model, 'purchase_order_id', payload.map(p => p.id))
      this.$set(this.model, 'selected_purchase_orders', payload)
      if (!payload.length) this.$set(this.model, 'trip_items', [])
    },
    onPurchaseItemSelected(payload) {
      let data = this.lodash.flatten(this.lodash.map(this.model.selected_purchase_orders, item => item.purchase_items))
      if (data.length) {
        data = data.filter(i => i)
        data = data.map(function (dta) {
          let newQuantity = (dta.quantity - (dta.trip_item_total_quantity + dta.sale_item_total_quantity))
          let newData = {
            ...dta,
            purchase_item_id: dta.id,
            quantity: newQuantity,
            total_price: newQuantity * dta.unit_price
          }
          delete newData.id
          return newData
        })
        data = data.filter(function (item) {
          return payload.find(i => (i === item.purchase_item_id))
        })
        let tripItems = [...this.model.trip_items]
        if (tripItems.length) {
          data = data.map(function (dta) {
            let tripItem = tripItems.find(i => i.purchase_item_id === dta.purchase_item_id)
            if (tripItem) return tripItem
            else return dta
          })
        }
      }
      this.model.trip_items = data
    },
    onChangeFile (event, index) {
      let vm = this
      let reader = new FileReader()
      let data = event.target.files[0]
      if (!data) {
        setTimeout(function() {
          vm.model.files[0].file = vm.firstFile[0].file
          return null
        }, 1000);
      }
      reader.readAsDataURL(data)
      reader.onload = () => {
        let file = {
          ...this.model.files[index],
          preview: reader.result,
          format: data.type,
          name: data.name
        }
        this.$set(this.model, 'files', [file]);
        this.$set(this, 'firstFile', [{...file}]);
      };
    },
    updateTotalPriceItem(index) {
      this.model.trip_items[index].total_price = this.model.trip_items[index].quantity * this.model.trip_items[index].unit_price
    },
    dataURLtoFile(dataUrl, filename) {
      let arr = dataUrl.split(','),
          mime = arr[0].match(/:(.*?);/)[1],
          bstr = atob(arr[1]),
          n = bstr.length,
          u8arr = new Uint8Array(n);
      while(n--){
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, {type:mime});
    },
    getPurchaseItemTableItems(purchaseOrders) {
      let items  = []
      purchaseOrders.forEach(purchaseOrder => {
        items.push({
          $checkbox: true,
          $purchaseOrder: true,
          purchase_order_id: purchaseOrder.id,
          product: purchaseOrder.product,
        })
        purchaseOrder?.purchase_items?.forEach((purchaseItem, index) => {
          if (this.model.id) {
            items.push({
              ...purchaseItem,
              $checkbox: true,
              $purchaseOrder: false,
              customer_id: purchaseOrder.customer_id,
              disabled: purchaseItem.available_quantity <= 0,
              _rowVariant: purchaseItem.available_quantity <= 0 ? 'secondary' : '',
              _index: index,
            })
          } else {
            items.push({
              ...purchaseItem,
              $purchaseOrder: false,
              customer_id: purchaseOrder.customer_id,
              $checkbox: true,
              disabled: purchaseItem.available_quantity <= 0,
              _rowVariant: purchaseItem.available_quantity <= 0 ? 'secondary' : '',
              _index: index,
            })
          }
          if(this.editInit){
            const tripItem = this.model.initial_trip_items.find((tripItem) => tripItem.purchase_item_id === purchaseItem.id);
            if (tripItem){
              items[items.length - 1].$selected = true;
            }
          }
        })

      })

      return items
    },
    handleChangeQuantity(tripItemIndex) {
      this.updateTotalPriceItem(tripItemIndex)
      this.setTotalQuantity()
    },
    handleChangePurchaseItems(purchaseItems) {
      this.model.purchase_items = purchaseItems
      this.editInit = false;
      this.model.isTripItemChanged = true;
    }
  },

}
</script>
